import { Communicator } from '../../tools/Communication';
import { toCsvBlob } from '@/utils/data';
import { ErrorDataType } from '@/utils/dpdApi';
import { getEnvironment, Environment } from '@/utils/env';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import {
  MerchantOrder,
  Revision,
  MerchantOrderItemType,
  getSlackLink,
  MerchantOrderItemOption,
  IdName,
  requiredDeliveryAddressAttributes,
  MerchantOrderType,
  MerchantOrderItemStatus,
  MerchantOrderStatus,
  handleLocalOpsStatusChange,
  sendSlackMessageForPhoneOrder,
  DeliveryAddress,
  MerchantOrderItem,
  StoreAssetOrderItem,
} from './HandleStatusChange';
import {
  SlackUrlObject,
  getDeliveryAddress,
  checkDeliveryAddress,
  deliveryAddressToDpdAddress,
  ContactInfo,
} from './Utils';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { Routes } from '@/utils/routes';
import { UNTRACKED_OTHER_ITEM_TYPE_ID } from '@/views/Order/utils/opsOrderLineRules';

function getSlackMessageForUpdate(order: MerchantOrder, oldRevision: Revision) {
  const translate = i18n.getFixedT('en');
  const url = `https://${
    getEnvironment() === Environment.Production
      ? 'appadmin.vytal.org'
      : 'appadmin.testing.internal.vytal.org'
  }${Routes.orderDetails(order.id)}`;
  const type = translate(`MerchantOrder:Type:${order.revision.type}`);
  const containerIds = Object.values(MerchantOrderItemType);
  let changes = '';
  for (const item of order.revision.items) {
    if (containerIds.includes(item.typeId)) {
      const oldItem = oldRevision.items.find((i) => i.typeId == item.typeId);
      if (oldItem?.amount != item.amount) {
        changes += `\t${item.name}: (${item.amount} from ${
          oldItem?.amount || 0
        })\n`;
      }
    }
  }
  const deletedItems = oldRevision.items
    .filter((i) => containerIds.includes(i.typeId))
    .filter(
      (i) => !order.revision.items.map((i2) => i2.typeId).includes(i.typeId),
    );
  for (const item of deletedItems) {
    changes += `\t${item.name}: (0 from ${item.amount})\n`;
  }

  return changes != ''
    ? `${order.storeName} - ${getSlackLink(url, type)}\n` +
        `\tcreated on ${new Date(order.createdTimestamp).toLocaleDateString(
          'en',
          getDateFormatOptions(false, 'en'),
        )}\n` +
        `\tto be executed on ${new Date(
          order.revision.orderExecutionDate,
        ).toLocaleDateString('en', getDateFormatOptions(false, 'en'))}\n` +
        changes +
        `\tby ${order.revision.createdByUserName}`
    : null;
}

function sendSlackMessageForUpdate(
  order: MerchantOrder,
  oldRevision: Revision,
  slackUrls: { Id: number; SlackUrl: string }[],
) {
  const slackUrl = slackUrls.find((r) => r.Id === order.regionId);
  if (slackUrl?.SlackUrl != null) {
    const text = getSlackMessageForUpdate(order, oldRevision);
    if (text != null) {
      fetch(slackUrl.SlackUrl, {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
    }
  }
}

function getSlackMessageForCreation(order: MerchantOrder) {
  const translate = i18n.getFixedT('en');
  const url = `https://${
    getEnvironment() === Environment.Production
      ? 'appadmin.vytal.org'
      : 'appadmin.testing.internal.vytal.org'
  }${Routes.orderDetails(order.id)}`;
  const type = translate(`MerchantOrder:Type:${order.revision.type}`);
  let itemAmounts = order.revision.items
    .filter((i) => i.typeId > 0 && parseInt(i.amount as string) > 0)
    .reduce((acc, cur) => acc + `\t${cur.name}: ${cur.amount}\n`, '');

  return (
    `${order.storeName} - ${getSlackLink(url, type)}\n` +
    `\tcreated on ${new Date(order.createdTimestamp).toLocaleDateString(
      'en',
      getDateFormatOptions(false, 'en'),
    )}\n` +
    `\tto be executed on ${new Date(
      order.revision.orderExecutionDate,
    ).toLocaleDateString('en', getDateFormatOptions(false, 'en'))}\n` +
    itemAmounts +
    `\tby ${order.revision.createdByUserName}`
  );
}

export function sendSlackMessageForCreation(
  order: MerchantOrder,
  slackUrls: { Id: number; SlackUrl: string }[],
) {
  const slackUrl = slackUrls.find((r) => r.Id === order.regionId);
  if (slackUrl?.SlackUrl != null) {
    return fetch(slackUrl.SlackUrl, {
      method: 'POST',
      body: JSON.stringify({ text: getSlackMessageForCreation(order) }),
    });
  }
}

async function downloadAsCsv(data: any) {
  const blob = toCsvBlob(data);
  const a = window.document.createElement('a');
  a.href = window.URL.createObjectURL(blob);
  a.download = `${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export async function submitOrUpdateOrder(
  revision: Revision,
  store: any,
  slackUrls: SlackUrlObject[],
  orderItemOptions: MerchantOrderItemOption[],
  regions: IdName[],
  orderId: string | null,
  country: string | undefined,
  hasAppUsers: boolean,
  checksAlreadyExecuted = false,
  storeAssetItems: StoreAssetOrderItem[] = [],
): Promise<boolean | undefined> {
  const deliveryAddress = getDeliveryAddress(store.delivery_address, country);
  let submit = true;
  let errors: ErrorDataType[] = [];
  if (!checksAlreadyExecuted) {
    submit = await checkDeliveryAddress(
      deliveryAddressToDpdAddress(deliveryAddress),
      store.id,
      revision.orderExecutionDate,
    );
    errors = testOrder(store, deliveryAddress, revision, hasAppUsers);
  }
  for (const error of errors) {
    alert(error.errorMsgLong);
  }
  if (submit && errors.length === 0) {
    const newRevision = { ...revision };
    // STORE_ASSET lines are edited in their own collection (they all share
    // typeId 0, so they cannot live in the typeId-indexed list) and are
    // flattened in here only for the wire. Shape matches what colugo writes:
    // TypeId 0, ItemTypeId set, Status Delivery.
    const storeAssetRows: MerchantOrderItem[] = storeAssetItems.map((a) => ({
      typeId: 0,
      itemTypeId: a.itemTypeId,
      name: a.name,
      amount: a.amount,
      status: MerchantOrderItemStatus.Delivery,
    }));
    newRevision.items = [...newRevision.items, ...storeAssetRows]
      .map((i) => ({
        ...i,
        amount: i.amount.toString(),
        amountInt: parseInt(i.amount as string),
      }))
      .filter((i) => i.amountInt !== 0 && !Number.isNaN(i.amountInt));
    newRevision.type = parseInt(newRevision.type as string);
    newRevision.status = parseInt(newRevision.status as string);
    newRevision.parcelCount = parseInt(newRevision.parcelCount as string);
    newRevision.packageWeights =
      (newRevision.packageWeights?.map((w) => parseInt(w)) as any[]) || [];

    newRevision.deliveryAddress = deliveryAddress;
    // Include region on revision if set in UI
    if (revision.regionId != null) {
      newRevision.regionId = revision.regionId;
    }
    let order;
    let response;
    if (orderId == null) {
      response = await Communicator.PostData('/MerchantOrder/Create', {
        storeId: store.external_vytal_id,
        revision: newRevision,
      });
      order = await response.json();
      sendSlackMessageForCreation(order, slackUrls);
    } else {
      newRevision.revisionId++;
      response = await Communicator.PostData(
        '/MerchantOrder/Update/' + revision.merchantOrderId,
        newRevision,
      );
      const {
        order: localOrder,
        oldRevision,
      }: { order: MerchantOrder; oldRevision: Revision } =
        await response.json();
      order = {
        ...localOrder,
        revision: {
          ...localOrder.revision,
          orderExecutionDate: new Date(localOrder.revision.orderExecutionDate),
        },
      };
      sendSlackMessageForUpdate(order, oldRevision, slackUrls);
    }
    if (!response.ok) {
      alert('Error submitting order');
      return false;
    } else {
      if (
        orderId != null &&
        (newRevision.status as number) === MerchantOrderStatus.PlannedOnTour
      ) {
        const data = handleLocalOpsStatusChange(
          [order],
          slackUrls,
          orderItemOptions,
          regions,
        );
        await downloadAsCsv(data);
      }
      if (
        (newRevision.status as number) ===
        MerchantOrderStatus.ApprovedForPackageDelivery
      ) {
        sendSlackMessageForPhoneOrder([order]);
      }
      return true;
    }
  }
}

// Only outbound orders ship something that has to be picked; the inbound types
// describe what comes back and may carry no line. Same split as colugo's
// OUTBOUND_MERCHANT_ORDER_TYPES.
const ORDER_TYPES_WITHOUT_LINE_REQUIREMENT = new Set<number>([
  MerchantOrderType.Undefined,
  MerchantOrderType.Pickup,
  MerchantOrderType.Termination,
  MerchantOrderType.Inventory,
  MerchantOrderType.DPDPickup,
  MerchantOrderType.DPDShopReturn,
]);

function lineAmount(item: MerchantOrderItem): number {
  const amount = parseInt(String(item.amount), 10);
  return Number.isNaN(amount) ? 0 : amount;
}

export function testOrder(
  store: any,
  deliveryAddress: DeliveryAddress,
  revision: Revision,
  hasAppUsers: boolean,
) {
  const errors: ErrorDataType[] = [];
  for (let i = 0; i < requiredDeliveryAddressAttributes.length; i++) {
    if (
      (deliveryAddress as any)[requiredDeliveryAddressAttributes[i]] == null ||
      (deliveryAddress as any)[requiredDeliveryAddressAttributes[i]].trim()
        .length === 0
    ) {
      errors.push({
        internalId: store.id,
        errorCode: 'alertAddressNotComplete',
        errorId: 1,
        errorMsgLong: i18n.t('MerchantOrder:alertAddressNotComplete', {
          attribute: requiredDeliveryAddressAttributes[i],
        }),
        errorMsgShort: requiredDeliveryAddressAttributes[i],
      });
    }
  }
  if (revision.type === MerchantOrderType.DPDPickup) {
    if (
      deliveryAddress.phoneNumber == null ||
      deliveryAddress.phoneNumber.trim().length === 0
    ) {
      errors.push({
        internalId: store.id,
        errorCode: 'alertAddressNotComplete',
        errorId: 1,
        errorMsgLong: i18n.t('MerchantOrder:alertAddressNotComplete', {
          attribute: 'phone',
        }),
        errorMsgShort: 'phone',
      });
    }
  }
  // Lines with amount 0 are dropped before the order is sent (see
  // submitOrUpdateOrder), so only the others count.
  const lines = revision.items.filter((i) => lineAmount(i) !== 0);
  const requiresLines = !ORDER_TYPES_WITHOUT_LINE_REQUIREMENT.has(
    parseInt(String(revision.type), 10),
  );
  if (requiresLines && lines.length === 0) {
    errors.push({
      internalId: store.id,
      errorCode: 'alertNoItems',
      errorId: 1,
      errorMsgLong: i18n.t('MerchantOrder:alertNoItems'),
      errorMsgShort: 'No items',
    });
  }
  if (
    requiresLines &&
    lines.some(
      (i) => i.itemTypeId == null && i.typeId === UNTRACKED_OTHER_ITEM_TYPE_ID,
    ) &&
    isNullOrWhitespace(revision.internalComment)
  ) {
    errors.push({
      internalId: store.id,
      errorCode: 'alertOtherItemNeedsComment',
      errorId: 1,
      errorMsgLong: i18n.t('MerchantOrder:alertOtherItemNeedsComment'),
      errorMsgShort: 'Other needs OPS comment',
    });
  }
  if (store.type == null) {
    errors.push({
      internalId: store.id,
      errorCode: 'alertNoStoreType',
      errorId: 1,
      errorMsgLong: i18n.t('MerchantOrder:alertNoStoreType'),
      errorMsgShort: 'No store type',
    });
  }
  if (!hasAppUsers) {
    errors.push({
      internalId: store.id,
      errorCode: 'alertNoAppAccounts',
      errorId: 1,
      errorMsgLong: i18n.t('MerchantOrder:alertNoAppAccounts'),
      errorMsgShort: 'No app accounts',
    });
  }
  if (
    parseInt(revision.type as string) === MerchantOrderType.Pickup &&
    (isNullOrWhitespace(deliveryAddress.email) ||
      isNullOrWhitespace(deliveryAddress.phoneNumber))
  ) {
    errors.push({
      internalId: store.id,
      errorCode: 'alertNoContact',
      errorId: 1,
      errorMsgLong: i18n.t('MerchantOrder:alertNoContact'),
      errorMsgShort: 'No Zoho contact',
    });
  }
  return errors;
}
